import { CreditTxWithMeta, OrgFullDetail } from "@garther/interfaces";
import { Box, Button, CreditTxItem, Input, Progress, Separator } from "@garther/ui/components";
import { refLink, toast, useI18n } from "@garther/ui/libs";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Copy, Info, Share2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useReferralStats } from "@/hooks/queries/use-referral-stats";
import { trackPaymentStart } from "@/libs/analytics";
import { creditApiClient } from "@/libs/api";
import { CLIENT_BASE_URL } from "@/libs/configs";

const MAX_TRANSACTIONS_DISPLAY = 20;

interface OrgReferralProps {
  org: OrgFullDetail;
}

export function OrgReferral({ org }: OrgReferralProps) {
  const { formatCredit, t } = useI18n();
  const { data: referralStats } = useReferralStats({ orgId: org.id });

  const orgCredit = useQuery({
    queryKey: ["org-credit", org.id],
    queryFn: () => creditApiClient.getUserCredits(org.id)
  });

  const orgCreditTxs = useQuery({
    queryKey: ["org-credit-transactions", org.id],
    queryFn: () => creditApiClient.getUserTransactions(org.id)
  });

  const handleTransactionPayment = useCallback((transaction: CreditTxWithMeta) => {
    trackPaymentStart(Math.abs(transaction.amount), "VND");
  }, []);

  const [_showInfoModal, setShowInfoModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(org.account?.referralCode || "");
    setCopied(true);
    toast.success(t("credits.wallet.referralCodeCopied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const referralLink = useMemo(() => {
    if (org.account?.referralCode)
      return refLink(`${CLIENT_BASE_URL}/org/${org.id}`, org.account?.referralCode);
    return "";
  }, [org.account?.referralCode, org.id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success(t("credits.wallet.referralLinkCopied"));
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t("credits.wallet.shareTitle"),
          text: t("credits.wallet.shareText"),
          url: referralLink
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="w-full">
      {/* Main Content */}
      <div className="w-full py-4">
        <div className=" space-y-4">
          <div className="flex gap-4 flex-col sm:flex-row">
            <Box className="flex-1 flex flex-col gap-6 relative justify-between">
              <div className="flex flex-col items-stretch justify-between gap-4">
                <p className="text-text-primary text-responsive-md font-normal">
                  {t("credits.wallet.summary")}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between border-b border-border pb-1">
                    <p className="text-text-secondary text-responsive-base font-normal">
                      {t("credits.wallet.balance")}
                    </p>
                    <p>{formatCredit(orgCredit.data?.balance || 0)}</p>
                  </div>
                  <div className="flex items-center justify-between border-b border-border pb-1">
                    <p className="text-text-secondary text-responsive-base font-normal">
                      {t("credits.wallet.totalEarnPending")}
                    </p>
                    <p>
                      {formatCredit(
                        (referralStats?.level1EarnPending || 0) + (referralStats?.level2EarnPending || 0)
                      )}
                    </p>
                  </div>

                  <div className="flex items-center justify-between border-b border-border pb-1">
                    <p className="text-text-secondary text-responsive-base font-normal">
                      {t("credits.wallet.totalReferred")}
                    </p>
                    <p>{(referralStats?.level1Count || 0) + (referralStats?.level2Count || 0)}</p>
                  </div>
                </div>
              </div>
            </Box>
            {/* Your Referral Code */}
            <Box className="flex-1 bg-background-secondary border-border p-6 sm:p-8 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-text-primary text-responsive-md font-normal">
                  {t("credits.wallet.yourReferralCode")}
                </p>
                <Button
                  variant="ghost"
                  padding="py-0.5 pl-2"
                  onClick={() => setShowInfoModal(true)}
                  className="text-text-primary hover:bg-background-highlight"
                >
                  <Info className="h-4 w-4" />
                </Button>
              </div>

              {/* Code Display */}
              <div className="bg-background-secondary border border-border p-3 text-center space-y-1">
                <p className="text-text-secondary text-responsive-sm">{t("credits.wallet.shareThisCode")}</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-text-primary text-responsive-xl font-mono tracking-wider">
                    {org.account?.referralCode}
                  </code>
                  {copied && <CheckCircle2 className="h-6 w-6 text-success-500" />}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid sm:grid-cols-2 gap-3">
                <Button onClick={handleCopyCode} padding="py-2">
                  <Copy className="h-4 w-4 mr-2" />
                  {t("credits.wallet.copyCode")}
                </Button>
                <Button onClick={handleShare} variant="outline" padding="py-2">
                  <Share2 className="h-4 w-4 mr-2" />
                  {t("credits.wallet.shareLink")}
                </Button>
              </div>

              <Separator className="bg-border" />

              {/* Referral Link */}
              <div className="space-y-0">
                <p className="text-text-secondary text-responsive-sm font-medium">
                  {t("credits.wallet.yourReferralLink")}
                </p>
                <div className="flex gap-2 w-full">
                  <Input
                    containerClassName="flex-1"
                    value={referralLink}
                    readOnly
                    className="bg-background-secondary border-border text-text-primary text-responsive-base"
                  />
                  <Button
                    onClick={handleCopyLink}
                    variant="outline"
                    className="border-border text-text-primary hover:bg-background-highlight shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Box>
          </div>

          {/* Network Growth */}
          <Box className="bg-background-secondary border-border p-6">
            <div className="space-y-4">
              <h3 className="text-text-primary text-responsive-md font-normal">
                {t("credits.wallet.networkGrowth")}
              </h3>

              <div className="space-y-3">
                <div className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary text-responsive-sm font-medium">
                      {t("credits.wallet.level1Direct")}
                    </span>
                    <span className="text-text-primary text-responsive-base font-normal">
                      {referralStats?.level1Count || 0} {t("credits.wallet.users")}
                    </span>
                  </div>
                  <Progress
                    value={
                      ((referralStats?.level1Count || 0) /
                        ((referralStats?.level1Count || 0) + (referralStats?.level2Count || 0))) *
                      100
                    }
                    className="h-2 bg-background-highlight"
                  />
                </div>

                <div className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary text-responsive-sm font-medium">
                      {t("credits.wallet.level2Indirect")}
                    </span>
                    <span className="text-text-primary text-responsive-base font-normal">
                      {referralStats?.level2Count || 0} {t("credits.wallet.users")}
                    </span>
                  </div>
                  <Progress
                    value={
                      ((referralStats?.level2Count || 0) /
                        ((referralStats?.level1Count || 0) + (referralStats?.level2Count || 0))) *
                      100
                    }
                    className="h-2 bg-background-highlight"
                  />
                </div>
              </div>
            </div>
          </Box>

          <Box className="flex flex-col flex-1 gap-4 max-h-[90vh] sm:max-h-[70vh] overflow-y-auto">
            <h4 className="text-responsive-md text-text-primary font-normal mb-3">
              {t("credits.transactionHistory")}
            </h4>
            {orgCreditTxs.data?.length === 0 ? (
              <div className="text-center w-full h-full py-6">
                <p className="text-responsive-base font-normal text-text-secondary">
                  {t("credits.noTransactions")}
                </p>
              </div>
            ) : (
              <div className="w-full h-full overflow-y-auto">
                {orgCreditTxs.data?.map((transaction, index) => (
                  <CreditTxItem
                    key={transaction.id}
                    transaction={transaction}
                    className={`${index === orgCreditTxs.data?.length - 1 ? "" : "border-b border-border-light"}`}
                    onPaymentClick={handleTransactionPayment}
                  />
                ))}
              </div>
            )}
            {(orgCreditTxs.data?.length || 0) > MAX_TRANSACTIONS_DISPLAY && (
              <div className="text-center pt-3 w-full">
                <p className="text-responsive-sm font-normal text-text-secondary">
                  {t("credits.showingRecent", {
                    count: MAX_TRANSACTIONS_DISPLAY,
                    total: orgCreditTxs.data?.length || 0
                  })}
                </p>
              </div>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
}
